import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/data/site";
import { Seo } from "@/components/Seo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await login(email, password); navigate("/dashboard"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
      <Seo title="Logg inn – Helserebellen" description="Logg inn for å få tilgang til programmene dine." path="/login" noindex />
      <div className="hidden lg:block relative overflow-hidden bg-olive-deep">
        <img src={ASSETS.caroline} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-olive-deep via-olive-deep/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-cream">
          <p className="font-script text-4xl text-sage">Velkommen tilbake</p>
          <p className="font-serif text-3xl mt-3 leading-tight">Ta tilbake helsen – på egne premisser.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-olive/60 text-sm hover:text-terra mb-10"><ArrowLeft size={16} /> Til forsiden</Link>
          <img src={ASSETS.logo} alt="" className="w-16 mix-blend-multiply" />
          <h1 className="mt-6 font-serif text-5xl text-olive-deep">Logg inn</h1>
          <p className="mt-2 text-olive/60">Få tilgang til dine programmer.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <input required type="email" placeholder="E-post" value={email} data-testid="login-email"
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-olive/20 bg-white/60 px-5 py-4 focus:outline-none focus:border-terra transition-colors" />
            <input required type="password" placeholder="Passord" value={password} data-testid="login-password"
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-olive/20 bg-white/60 px-5 py-4 focus:outline-none focus:border-terra transition-colors" />
            {error && <p className="text-terra text-sm" data-testid="login-error">{error}</p>}
            <button disabled={loading} data-testid="login-submit"
              className="w-full rounded-full bg-terra px-6 py-4 text-cream uppercase text-[13px] tracking-[0.2em] hover:bg-olive-deep transition-colors disabled:opacity-60">
              {loading ? "Logger inn…" : "Logg inn"}
            </button>
          </form>
          <p className="mt-6 text-olive/60 text-sm">Ny her? <Link to="/register" className="text-terra underline">Opprett konto</Link></p>
        </motion.div>
      </div>
    </div>
  );
}
